import { deepCopy } from './utils'
import { buildLegacyEndRoster, normalizeEndRoster, normalizeRosterSubs, validatedEndRoster } from '../rosterAnalysis'

export function queryAutoRosterSignature(best: Best | null, numPeriods: NumPeriods, gameFormat: GameFormat) {
	if (!best) {
		return JSON.stringify({
			roster: [ ],
			endRoster: [ ],
			subs: [ ],
			nulled: [ ],
		})
	}

	const endRoster = normalizeEndRoster(
		best.roster,
		validatedEndRoster(best.endRoster, best.created, best.endRosterCreated),
		best.subs,
		numPeriods,
		gameFormat,
	)

	return JSON.stringify({
		roster: best.roster,
		endRoster,
		subs: best.subs,
		nulled: best.nulled,
	})
}

export function createAutoRosterPhenotype(
	best: Best,
	numPeriods: NumPeriods,
	gameFormat: GameFormat,
	useRollingSubs: number,
	fixedTransitions: FixedRosterTransitions = [ ],
): Phenotype {
	const fixedSourceIndexes = new Set(fixedTransitions.flatMap((endIndex, startIndex) => (
		typeof endIndex === 'number' ? [ startIndex ] : [ ]
	)))
	const normalizedSubs = normalizeRosterSubs(best.subs, best.roster.length, numPeriods, gameFormat)
	const mutableSubs: Subs = [ ]
	for (let i = 0; i < normalizedSubs.length; i += 2) {
		if (!fixedSourceIndexes.has(normalizedSubs[i]) && !fixedSourceIndexes.has(normalizedSubs[i + 1])) {
			mutableSubs.push(normalizedSubs[i], normalizedSubs[i + 1])
		}
	}

	return {
		roster: deepCopy(best.roster),
		subs: useRollingSubs > 0 ? mutableSubs : [ ],
		orangeman: best.orangeman,
		score: null,
	}
}

export function prepareAutoRosterStart(
	best: Best,
	startingPhenotype: Phenotype,
	fixedTransitions: FixedRosterTransitions,
	numPeriods: NumPeriods,
	gameFormat: GameFormat,
): Best {
	const created = Date.now()
	const endRoster = applyFixedRosterTransitions(
		startingPhenotype.roster,
		startingPhenotype.subs,
		fixedTransitions,
		numPeriods,
		gameFormat,
	)

	return {
		...best,
		...startingPhenotype,
		endRoster,
		endRosterCreated: created,
		created,
	}
}

export function deriveFixedRosterTransitions(props: {
	roster: RosterData
	endRoster?: RosterData
	subs?: Subs
	locks: boolean[]
	numPeriods: NumPeriods
	gameFormat: GameFormat
}): FixedRosterTransitions {
	const { roster, locks, numPeriods, gameFormat } = props
	const endRoster = normalizeEndRoster(roster, props.endRoster, props.subs, numPeriods, gameFormat)
	const playersPerQtr = roster.length / numPeriods
	const transitions: FixedRosterTransitions = new Array(roster.length).fill(null)

	if (!roster.length || !Number.isInteger(playersPerQtr)) {
		return transitions
	}

	const endIndexes = new Map<PlayerId, number>()
	for (let qtr = 0; qtr < numPeriods; qtr += 1) {
		const start = qtr * playersPerQtr
		const end = start + playersPerQtr
		endIndexes.clear()
		for (let i = start; i < end; i += 1) {
			endIndexes.set(endRoster[i], i)
		}

		const quarterTransitions = new Array<number>(playersPerQtr)
		for (let i = start; i < end; i += 1) {
			quarterTransitions[i - start] = endIndexes.get(roster[i]) ?? i
		}

		for (let i = start; i < end; i += 1) {
			if (!locks[i] || quarterTransitions[i - start] === i) {
				continue
			}
			transitions[i] = quarterTransitions[i - start]
		}
	}

	return transitions
}

export function applyFixedRosterTransitions(
	roster: RosterData,
	subs: Subs,
	fixedTransitions: FixedRosterTransitions,
	numPeriods: NumPeriods,
	gameFormat: GameFormat,
) {
	const baseEndRoster = buildLegacyEndRoster(roster, subs, numPeriods, gameFormat)
	const endRoster = roster.slice()
	const playersPerQtr = roster.length / numPeriods

	for (let qtr = 0; qtr < numPeriods; qtr += 1) {
		const start = qtr * playersPerQtr
		const end = start + playersPerQtr
		const assignedTargets = new Set<number>()
		const assignedPlayers = new Set<PlayerId>()

		for (let startIndex = start; startIndex < end; startIndex += 1) {
			const endIndex = fixedTransitions[startIndex]
			if (typeof endIndex === 'number') {
				endRoster[endIndex] = roster[startIndex]
				assignedTargets.add(endIndex)
				assignedPlayers.add(roster[startIndex])
			}
		}

		for (let endIndex = start; endIndex < end; endIndex += 1) {
			const player = baseEndRoster[endIndex]
			if (!assignedTargets.has(endIndex) && !assignedPlayers.has(player)) {
				endRoster[endIndex] = player
				assignedTargets.add(endIndex)
				assignedPlayers.add(player)
			}
		}

		const remainingPlayers = roster.slice(start, end).filter(player => !assignedPlayers.has(player))
		let nextPlayer = 0
		for (let endIndex = start; endIndex < end; endIndex += 1) {
			if (!assignedTargets.has(endIndex)) {
				endRoster[endIndex] = remainingPlayers[nextPlayer]
				nextPlayer += 1
			}
		}
	}

	return normalizeEndRoster(roster, endRoster, subs, numPeriods, gameFormat)
}
